// Toast-System (Provider + Hook + Renderer) — bewusst ohne Library.
//
// API:
//   const toast = useToast();
//   toast.success("Bestätigt");
//   toast.error("Fehlgeschlagen: ...");
//   toast.info("Verarbeite…");
//
// Toasts erscheinen rechts unten, schliessen sich nach 4-7s
// (laenger bei Fehlern), oder manuell ueber das ×. Mehrere Toasts
// stapeln sich.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import "./Toast.css";

type ToastVariant = "success" | "error" | "info";

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastAPI {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastAPI | null>(null);

const VARIANT_DURATION_MS: Record<ToastVariant, number> = {
  success: 3500,
  info: 4000,
  error: 7000, // Fehler laenger stehen lassen, der User soll's mitkriegen
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, variant, message }]);
      // Auto-Dismiss
      window.setTimeout(() => dismiss(id), VARIANT_DURATION_MS[variant]);
    },
    [dismiss]
  );

  const api = useMemo<ToastAPI>(
    () => ({
      success: (m) => push("success", m),
      error: (m) => push("error", m),
      info: (m) => push("info", m),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="bmd-toast-container" aria-live="polite">
        {toasts.map((t) => (
          <ToastView key={t.id} toast={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastAPI {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast() ausserhalb von <ToastProvider> aufgerufen — main.tsx pruefen."
    );
  }
  return ctx;
}

// --- Einzelner Toast ------------------------------------------------

function ToastView({
  toast,
  onClose,
}: {
  toast: ToastItem;
  onClose: () => void;
}) {
  // Slide-In Animation: enter-Klasse erst direkt nach Mount setzen,
  // damit die CSS-Transition greift.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const handle = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(handle);
  }, []);

  return (
    <div
      className={`bmd-toast bmd-toast-${toast.variant} ${
        entered ? "bmd-toast-enter" : ""
      }`}
      role={toast.variant === "error" ? "alert" : "status"}
    >
      <span className="bmd-toast-icon" aria-hidden="true">
        <ToastIcon variant={toast.variant} />
      </span>
      <span className="bmd-toast-message">{toast.message}</span>
      <button
        type="button"
        className="bmd-toast-close"
        onClick={onClose}
        aria-label="Hinweis schließen"
      >
        ×
      </button>
    </div>
  );
}

function ToastIcon({ variant }: { variant: ToastVariant }) {
  if (variant === "success") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (variant === "error") {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="16.5" x2="12" y2="16.5" />
      </svg>
    );
  }
  // info
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12" y2="8" />
    </svg>
  );
}
